import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { User, ShoppingBag, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  new: { label: "Новый", cls: "bg-accent/10 text-accent border-accent/30" },
  processing: { label: "В обработке", cls: "bg-warning/10 text-warning border-warning/30" },
  delivered: { label: "Доставлен", cls: "bg-success/10 text-success border-success/30" },
  cancelled: { label: "Отменён", cls: "bg-danger/10 text-danger border-danger/30" },
};

function ProfileTab() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
  }, [user]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await updateProfile(form);
    setBusy(false);
    if (res.ok) toast.success("Профиль сохранён");
    else toast.error(res.error);
  };

  return (
    <div className="rounded-2xl border border-line bg-white p-6">
      <h2 className="text-lg font-bold text-ink">Профиль</h2>
      <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="block md:col-span-2">
          <span className="mb-1 block text-xs font-semibold text-muted">Email</span>
          <input disabled value={user?.email || ""} className="w-full cursor-not-allowed rounded-lg border border-line bg-paper px-4 py-3 text-sm text-muted" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">ФИО</span>
          <input data-testid="profile-name" value={form.name} onChange={onChange("name")} className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold text-muted">Телефон</span>
          <input data-testid="profile-phone" value={form.phone} onChange={onChange("phone")} className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
        </label>
        <label className="block md:col-span-2">
          <span className="mb-1 block text-xs font-semibold text-muted">Адрес доставки</span>
          <input data-testid="profile-address" value={form.address} onChange={onChange("address")} className="w-full rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none focus:border-ink" />
        </label>
        <div className="md:col-span-2">
          <button
            data-testid="profile-save"
            disabled={busy}
            type="submit"
            className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft disabled:opacity-50"
          >
            {busy ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </form>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-muted">Загрузка…</div>;
  if (orders.length === 0) {
    return (
      <div className="rounded-2xl border border-line bg-white p-12 text-center">
        <ShoppingBag className="mx-auto text-muted-soft" size={32} />
        <h3 className="mt-4 text-lg font-bold text-ink">Заказов пока нет</h3>
        <p className="mt-1 text-sm text-muted">Оформите первый заказ и мы бережно доставим его.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="orders-list">
      {orders.map((o) => {
        const s = STATUS_LABEL[o.status] || STATUS_LABEL.new;
        return (
          <div key={o.id} data-testid={`order-${o.order_no}`} className="rounded-2xl border border-line bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-soft">Заказ</div>
                <div className="mt-1 text-lg font-extrabold tracking-tightest text-ink">{o.order_no}</div>
                <div className="mt-1 text-xs text-muted">{new Date(o.created_at).toLocaleString("ru-RU")}</div>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${s.cls}`}>{s.label}</span>
            </div>
            <div className="mt-4 divide-y divide-line border-y border-line">
              {o.items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-3 py-3 text-sm">
                  <img src={resolveImageUrl(it.image)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="font-semibold text-ink">{it.product_name}</div>
                    <div className="text-xs text-muted">{it.variation_label} × {it.quantity}</div>
                  </div>
                  <div className="font-semibold text-ink">{formatPrice(it.line_total)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="text-muted">{o.address}</div>
              <div className="text-lg font-extrabold text-ink">{formatPrice(o.total)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="account-page" className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Личный кабинет</div>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tightest text-ink md:text-5xl">
          Здравствуйте, {user?.name || "клиент"}
        </h1>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <nav className="flex flex-row gap-1 rounded-2xl border border-line bg-white p-2 lg:flex-col">
            <NavLink
              to="/account"
              end
              data-testid="tab-orders"
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"
                }`
              }
            >
              <ShoppingBag size={15} /> Мои заказы
            </NavLink>
            <NavLink
              to="/account/profile"
              data-testid="tab-profile"
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                  isActive ? "bg-ink text-white" : "text-ink hover:bg-black/5"
                }`
              }
            >
              <User size={15} /> Профиль
            </NavLink>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="tab-logout"
              className="mt-2 flex items-center gap-2 rounded-xl px-4 py-2.5 text-left text-sm font-semibold text-danger transition-colors hover:bg-danger/10"
            >
              <LogOut size={15} /> Выйти
            </button>
          </nav>
        </aside>
        <div className="lg:col-span-9">
          <Routes>
            <Route index element={<OrdersTab />} />
            <Route path="profile" element={<ProfileTab />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
